import React from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { useField } from 'formik';
import { colorsTheme } from 'resources/theme/styled/colors';
import { AddressFormItem } from '.';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { getPlaceholderForInputField, getPlaceholderForSelect } from '../utils';

// components
import FieldIssue from '../FieldIssue';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import OrganizationAddressTypeSelect, {
  IProps as OrganizationAddressTypeSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/OrganizationAddressTypeSelect';
import CountriesAutocomplete, {
  IProps as CountriesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import {
  Button,
  Card,
  Col,
  FormField,
  FormInput,
  Row,
  SectionIntro,
  FormInputProps,
} from '@ui';

interface IProps {
  index: number;
  fieldName: string;
  deleteCallback?: () => void;
}

const LocationForm = ({ index, fieldName, deleteCallback }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const [field] = useField<AddressFormItem>(fieldName);

  return (
    <StyledCard>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant="h5"
        title={t(
          'intake_form.entity_profile.locations_with_index_section_title',
          { index: index + 1 },
        )}
      />
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<OrganizationAddressTypeSelectProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].type`}>
                {t('intake_form.entity_profile.type_of_address')}
              </FieldIssue>
            }
            name={`${fieldName}.type`}
            component={OrganizationAddressTypeSelect}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CountriesAutocompleteProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].country`}>
                {t('intake_form.entity_profile.country')}
              </FieldIssue>
            }
            name={`${fieldName}.country`}
            component={CountriesAutocomplete}
            additionalProps={{
              initialValue: field.value.countryInitialOption,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].city`}>
                {t('intake_form.entity_profile.city')}
              </FieldIssue>
            }
            name={`${fieldName}.city`}
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].street`}>
                {t('intake_form.entity_profile.street')}
              </FieldIssue>
            }
            name={`${fieldName}.street`}
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].state`}>
                {t('intake_form.entity_profile.state')}
              </FieldIssue>
            }
            name={`${fieldName}.state`}
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue fieldId={`addresses["${field.value.id}"].postalCode`}>
                {t('intake_form.entity_profile.postal')}
              </FieldIssue>
            }
            name={`${fieldName}.postal`}
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
      </Row>
      {deleteCallback ? (
        <HideIfDisabledForm hideIfSubmitting>
          <Button size="large" onClick={deleteCallback}>
            {t('remove', { ns: 'common' })}
          </Button>
        </HideIfDisabledForm>
      ) : null}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  .ant-card-body {
    min-height: 0px;
    padding: ${({ theme }) => theme.paddingXl};
  }
`;

export default LocationForm;
